var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { CustomRedirect } from '@/components/functional/Link/CustomRedirect/CustomRedirect';
import Layout from '@/container/Layout';
import { HotlineMeta } from '@/feature/hotline/component/HotlineMeta';
import { HotlineProDetailViewLayoutSkeleton } from '@/feature/hotline/professional/detail/component/HotlineProDetailViewLayout';
import { useHotlineProDetailFetch } from '@/feature/hotline/professional/detail/hook/useHotlineProDetailFetch';
import { useHotlineProDetailNegotiateMessage } from '@/feature/hotline/professional/detail/hook/useHotlineProDetailNegotiateMessage';
import { useHotlineProDetailNegotiateAccept } from '@/feature/hotline/professional/detail/negotiate/hook/useHotlineProDetailNegotiateAccept';
import { useHotlineProDetailNegotiateCancel } from '@/feature/hotline/professional/detail/negotiate/hook/useHotlineProDetailNegotiateCancel';
import { useHotlineProDetailNegotiateFile } from '@/feature/hotline/professional/detail/negotiate/hook/useHotlineProDetailNegotiateFile';
import { useHotlineProDetailNegotiatePhone } from '@/feature/hotline/professional/detail/negotiate/hook/useHotlineProDetailNegotiatePhone';
import { useHotlineProDetailReschedulePro } from '@/feature/hotline/professional/detail/negotiate/hook/useHotlineProDetailReschedulePro';
import { HotlineProDetailNegotiateView } from '@/feature/hotline/professional/detail/negotiate/page/HotlineProDetailNegotiateView';
import { isFirstRepliedDetailData } from '@/feature/hotline/professional/detail/util/typeGuard';
import { useParams } from '@/hooks/lib/react-router/useParams';
import { useUnmountRef } from '@/hooks/lifecycle/useUnmountRef';
import { useTypeSelector } from '@/hooks/state/useTypeSelector';
import { pagePath } from '@/utils/router/pagePath';
const hotlineProPath = pagePath.hotline.professional;
/**
 * 匿名ホットライン コンサル詳細 チャットページ（ロジック部分のみ）
 * @description api データ取得, その他ロジック部分
 */
export const HotlineProDetailNegotiatePage = () => {
    var _a, _b, _c;
    const { id = '' } = useParams();
    const profile = useTypeSelector((state) => state.profile);
    const dispatch = useDispatch();
    const unmountRef = useUnmountRef();
    // データ取得
    const { detailData, fetchDetailData, isLoading, setDetailData } = useHotlineProDetailFetch({
        dispatch,
        id,
        unmountRef,
    });
    const detailOneData = detailData[id];
    const requestId = ((_a = detailOneData === null || detailOneData === void 0 ? void 0 : detailOneData.hotline_request) === null || _a === void 0 ? void 0 : _a.id) || null;
    const interviewId = ((_c = (_b = detailOneData === null || detailOneData === void 0 ? void 0 : detailOneData.hotline_request) === null || _b === void 0 ? void 0 : _b.hotline_request_interview) === null || _c === void 0 ? void 0 : _c.id) || null;
    /**
     * 既読ログ更新後のイベント
     */
    const onSuccessSubmitLog = useCallback(() => {
        if (!detailOneData) {
            return;
        }
        // stateを更新して画面上で既読状態にする（既読更新はコア機能に影響がない処理であり、かつ短期間で何度もトリガーされる可能性がある観点から、apiでデータの引き直し等はあえて行っていない）
        setDetailData((prevState) => {
            var _a;
            const hotlineRequest = (_a = prevState[id]) === null || _a === void 0 ? void 0 : _a.hotline_request;
            if (!hotlineRequest) {
                return prevState;
            }
            return Object.assign(Object.assign({}, prevState), { [id]: Object.assign(Object.assign({}, detailOneData), { hotline_request: Object.assign(Object.assign({}, hotlineRequest), { messages: hotlineRequest.messages.map((message) => (Object.assign(Object.assign({}, message), { is_unread: false }))), unread_count: 0 }) }) });
        });
    }, [detailOneData, id, setDetailData]);
    /**
     * 既読 送信処理
     */
    const { displayLastMessage, onSubmitMessage } = useHotlineProDetailNegotiateMessage({
        dispatch,
        id,
        onSuccessSubmitLog,
        onSuccessSubmitMessage: fetchDetailData,
        requestId,
        unmountRef,
    });
    const { onClickFileLink } = useHotlineProDetailNegotiateFile({
        dispatch,
        id,
        requestId,
        unmountRef,
    });
    /**
     * 日程承諾 送信処理
     */
    const { onSubmitAccept } = useHotlineProDetailNegotiateAccept({
        dispatch,
        id,
        interviewId,
        onSuccess: fetchDetailData,
        requestId,
    });
    /**
     * キャンセルモーダル送信処理
     */
    const { onSubmitCancel } = useHotlineProDetailNegotiateCancel({
        dispatch,
        id,
        onSubmitCallback: fetchDetailData,
    });
    /**
     * 日程変更 送信処理
     */
    const { onSubmitScheduleChange } = useHotlineProDetailReschedulePro({
        dispatch,
        id,
        onSubmitCallback: fetchDetailData,
    });
    /**
     * 電話番号更新処理
     */
    const { onSubmitPhone } = useHotlineProDetailNegotiatePhone({
        dispatch,
        id,
        interviewId,
        onSuccess: fetchDetailData,
        requestId,
    });
    // 詳細データ取得
    useEffect(() => {
        ;
        (() => __awaiter(void 0, void 0, void 0, function* () {
            if (!detailOneData) {
                yield fetchDetailData();
            }
        }))();
    }, [detailOneData, fetchDetailData]);
    if (isLoading && !detailOneData) {
        return (_jsx(Layout, Object.assign({ mainClassName: 'l-main' }, { children: _jsx(HotlineProDetailViewLayoutSkeleton, {}) })));
    }
    // データがない場合は一覧へリダイレクト
    if (!detailOneData) {
        return _jsx(CustomRedirect, { to: hotlineProPath.list() });
    }
    // 初回返信前の場合は詳細内容にリダイレクト
    if (!isFirstRepliedDetailData(detailOneData)) {
        return _jsx(CustomRedirect, { to: hotlineProPath.detail(id) });
    }
    return (_jsxs(_Fragment, { children: [_jsx(HotlineMeta, { relativePath: hotlineProPath.detailNegotiate(id), title: `${detailOneData.title} | PROFFIT` }), _jsx(Layout, Object.assign({ mainClassName: 'l-main' }, { children: _jsx(HotlineProDetailNegotiateView, { detailData: detailOneData, displayLastMessage: displayLastMessage, onClickFileLink: onClickFileLink, onSubmitAccept: onSubmitAccept, onSubmitCancel: onSubmitCancel, onSubmitMessage: onSubmitMessage, onSubmitPhone: onSubmitPhone, onSubmitScheduleChange: onSubmitScheduleChange, profile: profile }) }))] }));
};
